import { InfoRounded as Info } from '@mui/icons-material'
import { makeStyles } from '@mui/styles'
import { ApexOptions } from 'apexcharts'
import classNames from 'classnames'
import { useEffect, useState } from 'react'
import ReactApexChart from 'react-apexcharts'
import { useTranslation } from 'react-i18next'
import { newThemeObject } from '../../newTheme'
import CardWrapper from '../common/CardWrapper'
import { useCardHeaderStyles } from '../../styles/cards'
import CashFlowStats from './CashFlowStats'
import FixedIncomeStats from './FixedIncomeStats'
import { getClientTaxSummaryRequest } from '../../api/clientAPI'
import { ClientTaxSummary, CouponPayment, CurrencyAmount, ProfitLossDeal } from '../../redux/reducers/clients'
import { useSelector } from '../../redux/hooks/useSelector'
import { IMPERIUM_FINANCE } from '../../constants/businessTerritory'

const useStyles = makeStyles(theme => ({
  cardWrapper: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1
  },
  noteWrapper: {
    display: 'flex',
    borderRadius: newThemeObject.roundness.basic,
    backgroundColor: newThemeObject.palette.background.lightGray,
    color: newThemeObject.palette.text.gray,
    padding: newThemeObject.padding.smallWrapper,
    gap: theme.spacing(0.5),
    alignItems: 'center',
  },
  noteText: {
    margin: 0,
  },
  graphWrapper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'stretch',
    minWidth: 0,
  },
}))

interface AreaChartProps {
  graphLineIndex: number
  handleChangePortfolioAmount: (event: React.ChangeEvent<{}>, newValue: number) => void
  optionsGraphLine: ApexOptions
  seriesGraphLine: any
  index?: number
  handleChangeIndex?: (index: number) => void
  portfolio: any | null
  labelTab: any
  labels: any[]
}

interface PortfolioSummaryRowProps {
  portfolio: any | null
  labelTab: any
  labels: any[]
}

interface CashFlowCardProps extends PortfolioSummaryRowProps {
  clientId?: number
}

// Karta Shrnuti investicniho portfolia.
export const PortfolioHeroCard: React.FC<PortfolioSummaryRowProps> = ({ portfolio, labelTab, labels }) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const user = useSelector(state => state.auth.user)
  const isImperiumUser = !!(user && user.businessTerritoryId === IMPERIUM_FINANCE)

  let accountAmount =
    portfolio && portfolio.assetClassPosition && portfolio.assetClassPosition.filter((item: any) => item.type === 'MM').length > 0
      ? portfolio.assetClassPosition.filter((item: any) => item.type === 'MM')[0].amount
      : 0

  const TNumber = getFiltredItem(labels, portfolio, labelTab, accountAmount, 'amountCZK')
  const HSNumber = TNumber - ( labelTab === 0 ? accountAmount : 0)
  const LTVNumber = HSNumber ? Math.max(0, (HSNumber - TNumber) / HSNumber) : 0

  return (
    <CardWrapper className={classes.cardWrapper}>
      <FixedIncomeStats
        title={labelTab === 0 ? t('CASHFLOW') : labels[labelTab - 1].name}
        HSNumber={HSNumber}
        ABNumber={labelTab === 0 ? accountAmount : 0}
        TNumber={TNumber}
        OYNumber={getFiltredItem(labels, portfolio, labelTab, accountAmount, 'expectedAmountCZK')}
        isLabel={labelTab>0}
        showLTV={isImperiumUser}
        LTVNumber={LTVNumber}
        hideExpectedReturn={isImperiumUser}
      />
    </CardWrapper>
  )
}

// Karta Tok hotovosti.
export const CashFlowCard: React.FC<CashFlowCardProps> = ({ portfolio, labelTab, labels, clientId }) => {
  const classes = useStyles()
  const [securitiesSaleDeals, setSecuritiesSaleDeals] = useState<ProfitLossDeal[]>([])
  const [couponsAndDividends, setCouponsAndDividends] = useState<CurrencyAmount[]>([])
  const user = useSelector(state => state.auth.user)
  // Podrobny pohled s rozpisem vyplat kuponu zatim jen pro Imperium.
  const isImperiumUser = !!(user && user.businessTerritoryId === IMPERIUM_FINANCE)

  useEffect(() => {
    if (!clientId) {
      return
    }
    const now = new Date()
    const yearStart = new Date(now.getFullYear(), 0, 1)
    getClientTaxSummaryRequest(clientId, yearStart.toISOString(), now.toISOString())
      .then(response => {
        const taxSummary = response.data as unknown as ClientTaxSummary
        setSecuritiesSaleDeals(taxSummary?.deals || [])
        setCouponsAndDividends(
          sumCurrencyAmountsByCurrency([
            ...(taxSummary?.dividends || []),
            ...(taxSummary?.coupons || []),
            ...(taxSummary?.dividendsNoTax || []),
            ...(taxSummary?.couponsNoTax || []),
          ])
        )
      })
      .catch(error => {
        console.error(error)
      })
  }, [clientId])

  return (
    <CardWrapper className={classes.cardWrapper}>
      <CashFlowStats
        thisYear={getFiltredItem(labels, portfolio, labelTab, 0, 'incomeThisYearCZK')}
        nextYear={getFiltredItem(labels, portfolio, labelTab, 0, 'incomeNextYearCZK')}
        fiveYears={getFiltredItem(labels, portfolio, labelTab, 0, 'income5YearCZK')}
        tenYears={getFiltredItem(labels, portfolio, labelTab, 0, 'income10YearCZK')}
        securitiesSaleDeals={securitiesSaleDeals}
        couponsAndDividends={couponsAndDividends}
        couponPayments={isImperiumUser ? getCouponPayments(labels, portfolio, labelTab) : undefined}
      />
    </CardWrapper>
  )
}

// Karta grafu kumulativniho cashflow.
const AreaChart: React.FC<AreaChartProps> = ({
  optionsGraphLine,
  seriesGraphLine,
}) => {
  const classes = useStyles()
  const cardHeader = useCardHeaderStyles()
  const { t } = useTranslation()

  return (
    <CardWrapper className={classNames(classes.cardWrapper, classes.graphWrapper)}>
      <div className={cardHeader.header}>
        <p className={cardHeader.title}>{t('CUMULATIVE_CASHFLOW')}</p>
      </div>
      <ReactApexChart options={optionsGraphLine} series={seriesGraphLine} type="area" height={280} width="100%" />
    </CardWrapper>
  )
}

export default AreaChart

const sumCurrencyAmountsByCurrency = (amounts: CurrencyAmount[]): CurrencyAmount[] => {
  const totalsByCurrency: { [currency: string]: number } = {}
  amounts.forEach(({ currency, amount }) => {
    const key = String(currency)
    totalsByCurrency[key] = (totalsByCurrency[key] || 0) + amount
  })
  return Object.keys(totalsByCurrency).map(currency => ({ currency, amount: totalsByCurrency[currency] }))
}

// Pocet vyplat kuponu, ktery se vejde do podrobneho pohledu karty Cash-flow.
const COUPON_PAYMENTS_LIMIT = 10

// Rozpis vyplat kuponu. Bez stitku bere hotovy merge z BE, se stitkem sklada
// rozpis jen z pozic daneho stitku.
const getCouponPayments = (labels: any[], portfolio: any, labelTab: any): CouponPayment[] => {
  if (!portfolio || !portfolio.incomeCashFlow) return []
  if (labelTab === 0) return portfolio.incomeCashFlow.couponPayments || []

  const labelSecurities = labels && labels[labelTab - 1] ? labels[labelTab - 1].securities || [] : []
  const payments: CouponPayment[] = []
  ;(portfolio.incomeCashFlow.positions || []).forEach((position: any) => {
    if (!labelSecurities.some((security: any) => security.securityId === position.securityId)) return
    payments.push(...(position.couponPayments || []))
  })

  return payments.sort((first, second) => first.date.localeCompare(second.date)).slice(0, COUPON_PAYMENTS_LIMIT)
}

const getFiltredItem = (labels: any[], portfolio: any, labelTab: any, accountAmount: any, itemName: string) => {
  let result = 0
  if (labelTab === 0) {
    result =
      portfolio && portfolio.incomeCashFlow
        ? (portfolio.incomeCashFlow[itemName] || 0) + accountAmount
        : 0
  } else {
    if (labels && portfolio.incomeCashFlow.positions) {
      for (let i = 0; i < labels[labelTab - 1].securities.length; i++) {
        for (let j = 0; j < portfolio.incomeCashFlow.positions.length; j++) {
          if (labels[labelTab - 1].securities[i].securityId === portfolio.incomeCashFlow.positions[j].securityId) {
            result += portfolio.incomeCashFlow.positions[j][itemName]
          }
        }
      }
    }
  }
  return result
}
